// onpremise-storage-operations.ts (Updated)
import { Observable, from, throwError, Subject, of } from "rxjs";
import { map, catchError, takeUntil, tap } from "rxjs/operators";
import { HttpClient, HttpEventType } from "@angular/common/http";
import { IStorageOperations } from "./storage-operations.interface";
import { StorageService } from "src/app/core/services/storage.service";
import { DeploymentService } from "src/app/core/services/deployment.service";
import { EnvironmentService } from "src/app/core/services/environment.service";
import { FileTypes, Store } from "src/app/core/models/globals";
import { 
  StorageItem, 
  UploadProgress, 
  OperationResult, 
  FileOperationContext, 
  DownloadResult,
  UploadContext, 
  ProgressCallback
} from "src/app/core/models/storage-models.interface";
import { StorageDataType } from "src/app/core/models/storage-data-type";
import { ManageStorageLogsService } from "src/app/core/services/manage-storage-logs.service";
import { FileUploader, FileItem } from "ng2-file-upload";

export class OnPremiseStorageOperations implements IStorageOperations {
  
  constructor(
    private storageService: StorageService,
    private storageLogService: ManageStorageLogsService,
    private deploymentService: DeploymentService,
    private env: EnvironmentService,
    private http: HttpClient
  ) {}
  
  listFiles(path: string, context: FileOperationContext): Observable<StorageItem[]> {
    return this.storageService.ListFiles(path, context.deploymentName, context.nodeName)
      .pipe(
        map((data: StorageDataType[]) => data.map(item => ({
          name: item.name,
          type: item.type === 'directory' ? 'directory' as const : 'file' as const,
          path: item.path,
          owner: item.owner,
          date: item.date ? new Date(item.date) : undefined,
          size: item.size,
          lastModified: item.date ? new Date(item.date) : undefined
        }))),
        catchError(error => throwError(() => new Error(`Failed to list files: ${error.message}`)))
      );
  }
  
  uploadFile(file: File, destinationPath: string, context: FileOperationContext, uniqueId: number, uploadContext?: UploadContext): 
  Observable<UploadProgress> {
    return new Observable<UploadProgress>(observer => {
      const regexObj = new RegExp(this.env.envVariables.fileUploadRegex || "^[A-Za-z0-9-_.@]+$");
      if (!file.name.match(regexObj)) {
        observer.error(new Error("File with special character and space cannot be uploaded."));
        return;
      }

      const currentUniqueId = uniqueId;
      const itemName = file.name;
      let uploadPath = destinationPath;
      const timeStarted = new Date();

      const headNodeIP = context.userInfo?.headNodeIP;
      const uploadPort = this.env.envVariables.BMFileUploadPORT;
      const fileUploadURL = `${this.env.envVariables.httpProtocol}${headNodeIP}:${uploadPort}/upload`;

      const newUploader = new FileUploader({
        url: fileUploadURL,
        headers: [
          { name: "destinationpath", value: uploadPath },
          { name: "requestUser", value: context.userInfo?.username || '' },
          { name: "islocalstore", value: "false" },
          { name: "vmname", value: context.nodeName || "null" },
          { name: "isfolder", value: "false" },
          { name: "filename", value: itemName }
        ],
        method: "post",
        removeAfterUpload: true,
      });

      if (context.activeStore === Store.WorkStore && this.deploymentService.isLocalStoreEnabled(context.deploymentName)) {
        newUploader.options.headers[2].value = "true"; // islocalstore
        newUploader.options.headers[3].value = context.nodeName; // vmname
      }

      const fileItem = new FileItem(newUploader, file, { url: fileUploadURL });
      fileItem.withCredentials = false;

      const cancelSubject = new Subject<void>();
      this.storageLogService.onPremiseUploadRequests[currentUniqueId] = cancelSubject;

      newUploader.onProgressItem = (item: any) => {
        const progress = item.progress || 0;
        
        const timeElapsed = new Date().valueOf() - timeStarted.valueOf();
        const uploadSpeed = (file.size * progress / 100) / (timeElapsed / 1000);
        const timeRemaining = Math.round((file.size - (file.size * progress / 100)) / uploadSpeed);
        
        this.storageLogService.updateTimeRemaining(currentUniqueId, isFinite(timeRemaining) ? timeRemaining : 0);
        this.storageLogService.updateStatusOfLog(currentUniqueId, `Uploaded ${progress}%`);
        this.storageLogService.updateStorageLogsStore();
        
        observer.next({
          loaded: Math.round((file.size * progress) / 100),
          total: file.size,
          percentage: progress,
          timeRemaining: isFinite(timeRemaining) ? timeRemaining : undefined,
          uploadSpeed: uploadSpeed
        });
      };

      newUploader.onSuccessItem = (item: any) => {
        this.storageLogService.updateTimeRemaining(currentUniqueId, null);
        this.storageLogService.updateRequestCompleteFields(currentUniqueId, "Upload Completed");
        this.storageLogService.updateStorageLogsStore();
        
        delete this.storageLogService.onPremiseUploadRequests[currentUniqueId];
        
        observer.next({ loaded: file.size, total: file.size, percentage: 100 });
        observer.complete();
      };

      newUploader.onErrorItem = (item: any, response: string, status: number) => {
        this.storageLogService.updateTimeRemaining(currentUniqueId, null);
        this.storageLogService.updateRequestCompleteFields(currentUniqueId, "Upload Failed");
        this.storageLogService.updateStorageLogsStore();
        
        delete this.storageLogService.onPremiseUploadRequests[currentUniqueId];
        
        observer.error(new Error(`Upload failed: ${response || 'Unknown error'}`));
      };

      cancelSubject.subscribe(() => {
        
        this.storageLogService.updateTimeRemaining(currentUniqueId, null);
        this.storageLogService.updateRequestCompleteFields(currentUniqueId, "Upload Cancelled");
        this.storageLogService.updateStorageLogsStore();
        
        newUploader.cancelAll();
        newUploader.clearQueue();
        
        delete this.storageLogService.onPremiseUploadRequests[currentUniqueId];
        
        // observer.error(new Error('Upload cancelled'));
        observer.complete();
      });

      newUploader.queue.push(fileItem);
      newUploader.uploadItem(fileItem);

      return () => {
        if (this.storageLogService.onPremiseUploadRequests[currentUniqueId]) {
          const cancelSubject = this.storageLogService.onPremiseUploadRequests[currentUniqueId];
          cancelSubject.next();
          cancelSubject.complete();
          delete this.storageLogService.onPremiseUploadRequests[currentUniqueId];
        }
      };
    });
  }
    
  downloadFile(filePath: string, context: FileOperationContext, uniqueId: number): Observable<DownloadResult> {
    return new Observable<DownloadResult>(observer => {
      const fileName = filePath.split('/').pop() || '';
      const isDirectory = context.fileType !== 'file';
      
      if (uniqueId) {
        this.storageLogService.createOnPremiseDownloadRequest(uniqueId);
        this.storageLogService.updateStatusOfLog(uniqueId, `Download started for ${fileName}`);
      }
      
      try {
        const downloadUrl = this.buildDownloadUrl(filePath, context, isDirectory);
        
        if (!downloadUrl) {
          observer.error(new Error('Failed to generate download URL'));
          return;
        }
        
        const cancelSubject = uniqueId ? this.storageLogService.onPremiseDownloadRequests[uniqueId] : new Subject<void>();
        
        this.http.get(downloadUrl, { 
          responseType: 'blob',
          observe: 'events',
          reportProgress: true
        }).pipe(
          takeUntil(cancelSubject)
        ).subscribe({
          next: (event) => {
            if (event.type === HttpEventType.DownloadProgress) {
              if (uniqueId && event.total) {
                const percentage = Math.round((event.loaded / event.total) * 100);
                this.storageLogService.updateStatusOfLog(uniqueId, `Downloaded ${percentage}%`);
                this.storageLogService.updateStorageLogsStore();
              }
            } else if (event.type === HttpEventType.Response) {
              const blob = event.body as Blob;
              
              if (uniqueId) {
                this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Completed");
                delete this.storageLogService.onPremiseDownloadRequests[uniqueId];
              }
              
              observer.next({ 
                blob: blob, 
                directDownload: true,
                fileName: isDirectory ? `${fileName}.zip` : fileName
              });
              observer.complete();
            }
          },
          error: (error) => {
            console.error(`Download error for ${fileName}:`, error);
            
            if (uniqueId) {
              delete this.storageLogService.onPremiseDownloadRequests[uniqueId];
              
              if (error.name === 'AbortError' || cancelSubject.closed) {
                this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Cancelled");
              } else {
                this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Failed");
              }
            }
            observer.error(new Error(`OnPremise download failed: ${error.message}`));
          }
        });
        
      } catch (error) {
        console.error(` Error initiating download for ${fileName}:`, error);
        if (uniqueId) {
          this.storageLogService.updateRequestCompleteFields(uniqueId, "Download Failed");
          delete this.storageLogService.onPremiseDownloadRequests[uniqueId];
        }
        observer.error(new Error(`Failed to initiate download: ${error.message}`));
      }
    });
  }
  
  private buildDownloadUrl(filePath: string, context: FileOperationContext, isDirectory: boolean): string {
    let path = filePath.replace("/mnt/", "");
    let vmName = context.nodeName;
    
    if (filePath.includes(this.env.envVariables.workStoreRoute) && 
        this.deploymentService.isLocalStoreEnabled(context.deploymentName)) {
      
      const tempWorkStoreRoute = this.env.envVariables.workStoreRoute.replace("/mnt/", "");
      
      if (vmName && vmName !== "") {
        path = path.replace(tempWorkStoreRoute, `${vmName}/${tempWorkStoreRoute}`);
        path = path.replace("All/", "");
      }
    }
    
    const headNodeIP = context.userInfo?.headNodeIP;
    const downloadPort = this.env.envVariables.BMFileDownloadPORT;
    const uploadPort = this.env.envVariables.BMFileUploadPORT;
    const routeViaSynchpc = context.userInfo?.routeViaSynchpc || false;
    
    let url = this.deploymentService.getUrlForUtilityService(
      context.deploymentName,
      headNodeIP,
      downloadPort,
      2, // operation type for download
      routeViaSynchpc,
      path
    );
    
    if (isDirectory) {
      url += "/zipDownload".toLowerCase(); 
      url = url.replace(path, filePath.substring(1)); 
      url = url.replace(downloadPort, uploadPort); 
    }
    
    return url;
  }

  private cancelDownload(uniqueId: number): boolean {
    this.storageLogService.cancelOnPremiseDownloadRequest(uniqueId);
    return true;
  }
  
  private getResponseType(fileName: string): string {
    const extension = fileName.split('.').pop()?.toLowerCase() || '';
    const isText = FileTypes.checkIfText(fileName, this.env.envVariables.TextFileExtension);
    const isJson = extension === "json";
    
    if (isJson) return "json";
    if (isText) return "text";
    return "blob";
  }
  
  deleteFile(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
    const fileName = filePath.split('/').pop() || '';
    const isDirectory = !fileName.includes('.');
    
    const storageDataType: StorageDataType = {
      name: fileName,
      type: isDirectory ? 'directory' : 'file',
      path: filePath
    };
    
    return this.storageService.DeleteData(context.deploymentName, storageDataType, context.nodeName || '')
      .pipe(
        map(() => ({ success: true, message: 'File deleted successfully' })),
        catchError(error => {
          const errorMessage = error.status === 405 
            ? 'You are not authorized to delete this file/folder'
            : `Delete failed: ${error.message}`;
          return throwError(() => new Error(errorMessage));
        })
      );
  }
  
  createDirectory(directoryName: string, path: string, context: FileOperationContext, uniqueId?: number): Observable<OperationResult> {
    let processedDirName = directoryName;
    let processedPath = path;
    
    if (processedPath === this.env.envVariables.syncStoreRoute) {
      processedPath = ".";
    }
    
    if (context.cloudType === 'On-Premise' || processedPath.includes(this.env.envVariables.workStoreRoute)) {
      if (path !== ".") {
        processedDirName = path + "/" + directoryName;
      }
    }
    
    const storageDataType: StorageDataType = {
      name: directoryName,
      type: "directory",
      path: path,
    };
    
    return this.storageService.CreateDirectory(context.deploymentName, storageDataType, context.nodeName || '')
    .pipe(
      map(() => ({ success: true, message: 'Directory created successfully' })),
      tap(() => {
        if (uniqueId) {
          this.storageLogService.updateRequestCompleteFields(uniqueId, "Completed");
        }
      }),
      catchError(error => {
        if (uniqueId) {
          this.storageLogService.updateRequestCompleteFields(uniqueId, "Failed");
        }
        return throwError(() => new Error(`OnPremise directory creation failed: ${error.message}`));
      })
    );
  }
  
  copyFile(sourcePath: string, destinationPath: string, context: FileOperationContext): Observable<OperationResult> {
    const copyData = {
      SourcePaths: [sourcePath],
      DestinationPath: destinationPath
    };
    
    return this.storageService.copyPasteFile(
      context.deploymentName,
      copyData,
      context.nodeName
    ).pipe(
      map(() => ({ success: true, message: 'File copied successfully' })),
      catchError(error => throwError(() => new Error(`Copy failed: ${error.message}`)))
    );
  }
  
  renameFile(oldPath: string, newName: string, context: FileOperationContext): Observable<OperationResult> {
    const isFile = oldPath.includes('.');
    const storageDataType: StorageDataType = {
      name: newName,
      type: isFile ? 'file' : 'directory',
      path: oldPath.substring(0, oldPath.lastIndexOf('/'))
    };
    
    return this.storageService.rename(
      context.deploymentName,
      storageDataType,
      oldPath,
      context.nodeName || ''
    ).pipe(
      map(() => ({ success: true, message: 'File renamed successfully' })),
      catchError(error => throwError(() => new Error(`Rename failed: ${error.message}`)))
    );
  }
  
  checkFileExists(filePath: string, context: FileOperationContext): Observable<boolean> {
    return this.listFiles(filePath.substring(0, filePath.lastIndexOf('/')), context)
      .pipe(
        map(files => {
          const fileName = filePath.split('/').pop();
          return files.some(file => file.name === fileName);
        }),
        catchError(() => throwError(() => false))
      );
  }
  
  generateDownloadUrl(filePath: string, context: FileOperationContext): Observable<string> {
    return throwError(() => new Error('Download URL generation not supported for OnPremise storage'));
  }
  
  readFileContent(filePath: string, context: FileOperationContext): Observable<string> {
    const pageObj = {
      totalLines: -1,
      pageSize: 1000000, // Large page size to get full content
      currentIndex: 0
    };
    
    return this.storageService.viewFileData(
      context.deploymentName,
      filePath,
      context.nodeName || '',
      pageObj,
      false // Don't convert to HTML
    ).pipe(
      map((response: any) => response?.content || ''),
      catchError(error => throwError(() => new Error(`Failed to read file content: ${error.message}`)))
    );
  }
  
  saveFileContent(filePath: string, content: string | File, context: FileOperationContext): Observable<OperationResult> {
    if (content instanceof File) {
      return this.uploadFile(content, filePath, context, Date.now()).pipe(
        map(() => ({ success: true, message: 'File saved successfully' }))
      );
    } else {
      this.storageService.writeFileData(
        context.deploymentName,
        filePath,
        context.nodeName || '',
        content
      );
      return new Observable<OperationResult>(observer => {
        observer.next({ success: true, message: 'File content saved successfully' });
        observer.complete();
      });
    }
  }
  
  zipFiles(filePaths: string[], zipPath: string, zipName: string, context: FileOperationContext, uniqueId: number): Observable<OperationResult> {
    // Create cancellation subject
    this.storageLogService.createOnPremiseZipRequest(uniqueId);
    
    return this.storageService.zipWithService(
      filePaths.join(','),
      zipPath,
      zipName,
      context.nodeName || '',
      context.userInfo?.headNodeIP || '',
      context.userInfo?.username || ''
    ).pipe(
      takeUntil(this.storageLogService.onPremiseZipRequests[uniqueId]),
      map(() => ({ success: true, message: 'Zip operation completed' })),
      catchError(error => {
        if (error.name === 'AbortError' || error.status === 499) {
          return throwError(() => new Error('Zip operation cancelled'));
        }
        return throwError(() => new Error(`Zip operation failed: ${error.message}`));
      })
    );
  }

  unzipFile(zipFilePath: string, extractPath: string, context: FileOperationContext, uniqueId: number, forceOverwrite: boolean = false): Observable<OperationResult> {
    return this.storageService.unZipWithService(
      zipFilePath,
      extractPath,
      context.nodeName || '',
      '', // vmIp will be resolved in service
      context.userInfo?.username || '',
      forceOverwrite
    ).pipe(
      map(() => ({ success: true, message: 'Unzip operation initiated' })),
      catchError(error => throwError(() => new Error(`Unzip operation failed: ${error.message}`)))
    );
  }

  
  cancelOperation(uniqueId: number, operationType: 'upload' | 'download' | 'zip' | 'unzip' | 'general'): boolean {
    switch (operationType) {
      case 'upload':
        return this.cancelUpload(uniqueId);
      case 'download':
        return this.cancelDownload(uniqueId);
      case 'zip':
        return this.cancelZip(uniqueId);
      case 'unzip':
        return this.cancelUnzip(uniqueId);
      default:
        return this.cancelGeneric(uniqueId);
    }
  }
  
  private cancelUpload(uniqueId: number): boolean {
    this.storageLogService.cancelOnPremiseUploadRequest(uniqueId);
    return true;
  }
  
  private cancelZip(uniqueId: number): boolean {
    this.storageLogService.cancelOnPremiseZipRequest(uniqueId);
    return true;
  }
  
  private cancelUnzip(uniqueId: number): boolean {
    this.storageLogService.cancelOnPremiseUnzipRequest(uniqueId);
    return false;
  }
  
  private cancelGeneric(uniqueId: number): boolean {
    this.storageLogService.updateRequestCompleteFields(uniqueId, "Operation Cancelled");
    return true;
  }

  verifyDirectoryPath(paths: string[], context: FileOperationContext): Observable<boolean> {
    return of(true);
  }

  viewFileData(filePath: string, context: FileOperationContext, uniqueId?: number): Observable<any> {
    const pageObj = {
      totalLines: context.pageData?.totalLines || -2,
      pageSize: context.pageData?.pageSize || 1000000, // Large page size to get full content
      currentIndex: context.pageData?.currentIndex || -1
    };
    
    return this.storageService.viewFileData(
      context.deploymentName,
      filePath,
      context.nodeName || '',
      pageObj,
      false // Don't convert to HTML
    ).pipe(
      map((response: any) => response?.content || response || ''),
      catchError(error => throwError(() => new Error(`Failed to view OnPremise file: ${error.message}`)))
    );
  }
}